import React, { useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import useFetchSettings from "../../hooks/useFetchSettings"
import { PageLoader } from "../Authentication/page-loader"
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react"
import { IoChevronDownOutline, IoCheckmark } from "react-icons/io5"

const NurseAcuityComponent = ({
	nurseStats = [],
	getCapacityLabel,
	adjustedMAFNAT,
	maxAcuity,
	viewScheduled,
	setViewScheduled
}) => {
	const [showFullReport, setShowFullReport] = useState(true) // Start expanded
	const bottomRef = useRef(null)
	const navigate = useNavigate()
	const { isLoading } = useAuth0()

	// Ensure nurseStats is an array and not empty
	const validNurseStats = Array.isArray(nurseStats) ? nurseStats : []

	const { settingsData, settingsStatus, settingsError } = useFetchSettings()

	const sortedNurseStats = validNurseStats.sort(
		(a, b) =>
			(viewScheduled ? b.currentScheduledAcuity : b.currentActualAcuity) -
			(viewScheduled ? a.currentScheduledAcuity : a.currentActualAcuity)
	)

	const totalAcuity = sortedNurseStats.reduce(
		(total, nurse) =>
			total + (viewScheduled ? nurse.currentScheduledAcuity : nurse.currentActualAcuity),
		0
	)
	const totalPatients = sortedNurseStats.reduce(
		(total, nurse) =>
			total + (viewScheduled ? nurse.currentScheduledPatients : nurse.currentActualPatients),
		0
	)
	const totalChangeInAcuity = sortedNurseStats.reduce(
		(total, nurse) =>
			total + (viewScheduled ? nurse.changeInScheduledAcuity : nurse.changeInActualAcuity),
		0
	)

	const averageAcuity =
		sortedNurseStats.length > 0 ? (totalAcuity / sortedNurseStats.length).toFixed(2) : 0
	const averagePatients =
		sortedNurseStats.length > 0 ? (totalPatients / sortedNurseStats.length).toFixed(2) : 0
	const averageChangeInAcuity =
		sortedNurseStats.length > 0 ? (totalChangeInAcuity / sortedNurseStats.length).toFixed(2) : 0

	// Determine how many nurses to show based on the toggle state
	const nursesToShow = showFullReport ? sortedNurseStats : sortedNurseStats.slice(0, 3)

	const handleToggleReport = () => {
		setShowFullReport(!showFullReport)
		if (!showFullReport) {
			setTimeout(() => {
				bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
			}, 0)
		}
	}

	const getChangeStyle = change => {
		if (change > 0) {
			return "text-green-600" // Positive change style
		} else if (change < 0) {
			return "text-red-600" // Negative change style
		} else {
			return "text-gray-500" // No change style
		}
	}

	const getArrowIcon = change => {
		if (change > 0) {
			return "↑" // Up arrow for positive change
		} else if (change < 0) {
			return "↓" // Down arrow for negative change
		} else {
			return "—" // Dash for no change
		}
	}

	const handleRedistributeClick = nurseName => {
		navigate(`/ic-tracker?nurseName=${nurseName}`)
	}

	const handleViewChange = view => {
		setViewScheduled(view === "Scheduled")
	}

	if (
		isLoading ||
		!settingsData ||
		settingsData.length === 0 ||
		settingsStatus === "loading" ||
		settingsError
	) {
		return <PageLoader /> // Show loading state while fetching
	}

	return (
		<>
			<div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
				<h3 className="text-xl font-medium text-gray-900">Current Nurse Workload</h3>

				{/* Dropdown for Scheduled/Actual View */}
				<div className="text-right mb-8 relative">
					<Menu as="div" className="relative inline-block text-left">
						<MenuButton className="inline-flex items-center justify-center w-full rounded-md bg-gray-200 text-gray-800 px-4 py-2 shadow-sm hover:bg-gray-300">
							{viewScheduled ? "Scheduled" : "Actual"}
							<IoChevronDownOutline className="w-5 h-5 text-gray-400 ml-2" />
						</MenuButton>
						<Transition
							enter="transition duration-100 ease-out"
							enterFrom="transform scale-95 opacity-0"
							enterTo="transform scale-100 opacity-100"
							leave="transition duration-75 ease-in"
							leaveFrom="transform scale-100 opacity-100"
							leaveTo="transform scale-95 opacity-0"
						>
							<MenuItems className="absolute left-0 z-10 mt-2 w-40 origin-top-left rounded-md bg-white shadow-lg focus:outline-none">
								<div className="py-1">
									{["Scheduled", "Actual"].map(option => (
										<MenuItem key={option}>
											{({ active }) => (
												<button
													onClick={() => handleViewChange(option)}
													className={`${
														active
															? "bg-gray-100 text-gray-900"
															: "text-gray-700"
													} flex items-center px-4 py-2 w-full text-left`}
												>
													{option}
													{((viewScheduled && option === "Scheduled") ||
														(!viewScheduled &&
															option === "Actual")) && (
														<IoCheckmark className="w-5 h-5 text-gray-500 ml-auto" />
													)}
												</button>
											)}
										</MenuItem>
									))}
								</div>
							</MenuItems>
						</Transition>
					</Menu>
				</div>

				{/* Column Headers */}
				<div className="grid grid-cols-12 gap-4 mb-2 mt-4 text-bold text-sm font-medium uppercase text-left">
					<div className="col-span-6">Nurse</div>
					<div className="col-span-2">Current Acuity</div>
					<div className="col-span-2">Current # Patients</div>
					{/* <div className="col-span-2">Change in Acuity (Next 30 Minutes)</div> */}
					<div className="col-span-2">
						{viewScheduled
							? "Change in Acuity (Next 30 Minutes)"
							: "Projected Change in Acuity (Next 30 Minutes)"}
					</div>
				</div>

				{/* List of nurses with their current acuities and other metrics */}
				<div className="mt-2">
					{nursesToShow.length > 0 ? (
						nursesToShow.map(nurse => {
							const currentAcuity = viewScheduled
								? nurse.currentScheduledAcuity
								: nurse.currentActualAcuity
							const currentPatients = viewScheduled
								? nurse.currentScheduledPatients
								: nurse.currentActualPatients
							const changeInAcuity = viewScheduled
								? nurse.changeInScheduledAcuity
								: nurse.changeInActualAcuity

							return (
								<div
									key={nurse.nurseId}
									className={`grid grid-cols-12 gap-4 items-center mb-4 text-left relative p-2 ${
										currentAcuity > adjustedMAFNAT
											? "border-2 border-red-500 bg-red-100"
											: ""
									}`}
								>
									<div className="col-span-6 flex items-center relative py-2 h-8">
										{/* Background bar showing up to maxAcuity */}
										<div
											className="absolute inset-0 bg-blue-100 rounded-sm h-full"
											style={{
												width: `${(currentAcuity / maxAcuity) * 100}%`
											}}
										></div>

										{/* Overlay indicating acuity beyond max capacity with cross-hatched styling */}
										{currentAcuity > adjustedMAFNAT && (
											<div
												className="absolute inset-y-0 bg-red-cross-hatched h-full"
												style={{
													left: `${(adjustedMAFNAT / maxAcuity) * 100}%`,
													width: `${((currentAcuity - adjustedMAFNAT) / maxAcuity) * 100}%`
												}}
											></div>
										)}

										{/* Nurse name and max acuity line */}
										<span className="relative text-bold text-md font-medium text-gray-900 pl-2">
											{nurse.nurseName}
										</span>
										{currentAcuity > adjustedMAFNAT && (
											<>
												{/* Line indicating max acuity */}
												<div
													className="absolute h-full z-10"
													style={{
														left: `${(adjustedMAFNAT / maxAcuity) * 100}%`
													}}
												>
													<div className="w-1 bg-black h-full"></div>
												</div>
											</>
										)}
									</div>

									<div className="col-span-2 text-md text-bold text-gray-900 flex items-center">
										{currentAcuity}
										{getCapacityLabel(currentAcuity, nurse)}
									</div>
									<div className="col-span-2 text-md text-bold text-gray-900">
										{currentPatients}
									</div>
									<div
										className={`col-span-2 text-md text-bold font-medium flex items-center ${getChangeStyle(changeInAcuity)}`}
									>
										<span>{Math.abs(changeInAcuity)}</span>
										<span className="ml-1">{getArrowIcon(changeInAcuity)}</span>
									</div>
									{/* Fix Button - Positioned outside the main container */}
									{currentAcuity > adjustedMAFNAT && (
										<button
											onClick={() => handleRedistributeClick(nurse.nurseName)}
											className="absolute -right-6 top-1/2 transform -translate-y-1/2 bg-white text-red-600 border border-red-600 px-4 py-1 font-medium text-md shadow-lg"
											style={{ width: "100px" }}
										>
											Adjust?
										</button>
									)}
								</div>
							)
						})
					) : (
						<div className="text-sm text-gray-500 text-center">
							No nurse data available.
						</div>
					)}
				</div>

				{/* Summary Statistics (Shown only when full report is expanded) */}
				{showFullReport && (
					<>
						<div className="grid grid-cols-12 gap-4 mb-4 mt-6 text-bold text-sm font-medium text-left">
							<div className="col-span-6 text-right">Totals:</div>
							<div className="col-span-2 text-sm text-gray-900">{totalAcuity}</div>
							<div className="col-span-2 text-sm text-gray-900">{totalPatients}</div>
							<div className="col-span-2 text-sm text-gray-900">
								{totalChangeInAcuity}
							</div>
						</div>

						<div className="grid grid-cols-12 gap-4 mb-4 mt-1 text-bold text-sm font-medium text-left">
							<div className="col-span-6 text-right">Averages:</div>
							<div className="col-span-2 text-sm text-gray-900">{averageAcuity}</div>
							<div className="col-span-2 text-sm text-gray-900">
								{averagePatients}
							</div>
							<div className="col-span-2 text-sm text-gray-900">
								{averageChangeInAcuity}
							</div>
						</div>
					</>
				)}

				{/* Divider Line */}
				<hr className="border-t border-gray-300 my-4" />

				<div className="text-center mt-2">
					<button
						className="text-customBlue text-sm font-medium"
						onClick={handleToggleReport}
					>
						{showFullReport
							? "Collapse Report"
							: `View full report (${nurseStats.length - 3} more)`}
					</button>
				</div>
			</div>

			{/* Invisible element used as a target for scrolling, placed right after the visible content */}
			<div ref={bottomRef} className="mt-6"></div>
		</>
	)
}

export default NurseAcuityComponent
